import { ReactNode } from 'react';

import { Icon } from '@/shared/types/icon';

import { CartesiaIcon } from '../../icons/Cartesia';
import { DeepgramIcon } from '../../icons/Deepgram';
import { ElevenLabsIcon } from '../../icons/ElevenLabs';
import { GoogleIcon } from '../../icons/Google';
import { MiniMaxIcon } from '../../icons/MiniMax';
import { RimeIcon } from '../../icons/Rime';
import { SonioxIcon } from '../../icons/Soniox';
import { SpeechModel, SpeechModelProvider, SpeechProvider } from '../../types';

export const SPEECH_PROVIDER_NAMES: Record<SpeechModelProvider, string> = {
  elevenlabs: 'ElevenLabs',
  cartesia: 'Cartesia',
  minimax: 'MiniMax',
  soniox: 'Soniox',
  deepgram: 'Deepgram',
  rime: 'Rime',
  google: 'Google',
};

const PROVIDER_ICON_COMPONENTS: Record<SpeechModelProvider, (props: Icon) => ReactNode> = {
  elevenlabs: ElevenLabsIcon,
  cartesia: CartesiaIcon,
  minimax: MiniMaxIcon,
  soniox: SonioxIcon,
  deepgram: DeepgramIcon,
  rime: RimeIcon,
  google: GoogleIcon,
};

/** A speech provider's logo at any size; nothing for a provider without one. */
export const SpeechProviderIcon = ({
  provider,
  className = 'size-4',
}: {
  provider: SpeechModelProvider;
  className?: string;
}) => {
  const ProviderIcon = PROVIDER_ICON_COMPONENTS[provider];
  return ProviderIcon ? <ProviderIcon className={className} /> : null;
};

export const SPEECH_PROVIDER_ICONS: Partial<Record<SpeechModelProvider, ReactNode>> =
  Object.fromEntries(
    Object.keys(PROVIDER_ICON_COMPONENTS).map((provider) => [
      provider,
      <SpeechProviderIcon key={provider} provider={provider as SpeechModelProvider} />,
    ])
  );

// The API allows several speech models per provider; the console keeps one.
export const getProviderOptions = (providers: SpeechProvider[], speechModels: SpeechModel[]) => {
  const used = new Set(speechModels.map(({ provider }) => provider));
  return providers
    .filter(({ speech_model_supported }) => speech_model_supported)
    .map(({ provider }) => ({ provider, disabled: used.has(provider) }));
};
